import { EVENTOS_INTERNOS, esEventoInterno, normalizarTipoEvento } from "../utils/eventosInternos";
﻿import React, { useEffect, useState, useRef } from "react";
import Timeline, { TimelineHeaders, DateHeader } from "react-calendar-timeline";
import "react-calendar-timeline/style.css";
import { format, startOfDay, startOfWeek, endOfWeek, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
    HiClock, HiExclamationTriangle, HiCheckBadge, HiCalendarDays,
    HiQuestionMarkCircle, HiWrench, HiNoSymbol, HiOutlineXMark, HiFunnel, HiMagnifyingGlass, HiArrowPath
} from "react-icons/hi2";
import { FaUmbrellaBeach } from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";

// función segura para formatear fechas
const safeFormat = (fecha, formato = "EEEE d 'de' MMMM yyyy") => {
    try {
        if (!fecha) return "Sin fecha";
        const soloFecha = fecha.split("T")[0];
        const date = parseISO(soloFecha);
        return format(date, formato, { locale: es });
    } catch {
        return "Sin fecha";
    }
};

const toJsDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === "function") return value.toDate();
    if (typeof value.valueOf === "function") {
        const ms = value.valueOf();
        if (typeof ms === "number") return new Date(ms);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMonthHeaderEsMx = ([start]) => {
    const date = toJsDate(start);
    if (!date) return "";
    return format(date, "MMMM yyyy", { locale: es });
};

const formatWeekHeaderEsMx = ([start]) => {
    const date = toJsDate(start);
    if (!date) return "";
    const inicioSem = startOfWeek(date, { weekStartsOn: 1 });
    const finSem = endOfWeek(date, { weekStartsOn: 1 });
    return `${format(inicioSem, "d", { locale: es })} – ${format(finSem, "d MMM", { locale: es })}`;
};

const formatDayHeaderEsMx = ([start]) => {
    const date = toJsDate(start);
    if (!date) return "";
    return format(date, "EEE d MMM", { locale: es });
};

// ── Constantes y helpers de módulo ───────────────────────────────────────────
const DAY_MS = 24 * 60 * 60 * 1000;

const API_RANGO_POR_VISTA = { dia: 'semana', semana: 'semana', mes: '30dias', mes_anterior: '30dias' };

function ComponenteGantt() {
    const { isAdmin } = useAuth();

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    const [density, setDensity] = useState(() => {
        try { return localStorage.getItem('gantt_density') || 'compact'; } catch { return 'compact'; }
    });
    const DENSITY_CONFIG = {
        comfortable: { lineHeight: 48, barHeight: 32, sidebarW: 240 },
        compact: { lineHeight: 32, barHeight: 20, sidebarW: 220 },
        ultra: { lineHeight: 22, barHeight: 14, sidebarW: 180 },
    };
    const currentDensity = DENSITY_CONFIG[density] ?? DENSITY_CONFIG.compact;
    const ALTURA_TARJETA_GANTT = currentDensity.barHeight;
    const [grupos, setGrupos] = useState([]);
    const [tareas, setTareas] = useState([]);
    const [visibleTimeStart, setVisibleTimeStart] = useState(() => {
        const hoy = startOfDay(new Date());
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            return hoy.getTime() + 7 * 60 * 60 * 1000; // 7:00 am
        }
        const d = hoy.getDay();
        const lunes = new Date(hoy);
        lunes.setDate(hoy.getDate() - (d === 0 ? 6 : d - 1));
        return lunes.getTime();
    });
    const [visibleTimeEnd, setVisibleTimeEnd] = useState(() => {
        const hoy = startOfDay(new Date());
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            return hoy.getTime() + 21 * 60 * 60 * 1000; // 9:00 pm
        }
        const d = hoy.getDay();
        const lunes = new Date(hoy);
        lunes.setDate(hoy.getDate() - (d === 0 ? 6 : d - 1));
        return lunes.getTime() + 7 * DAY_MS;
    });
    const [openModal, setOpenModal] = useState(false);
    const [tareaSeleccionada, setTareaSeleccionada] = useState(null);
    const [reservaCompleta, setReservaCompleta] = useState(null);
    const [itinerarioEdits, setItinerarioEdits] = useState([]);
    const [guardandoHorario, setGuardandoHorario] = useState(null); // id del itinerario guardando
    const [sucursalesFiltro, setSucursalesFiltro] = useState([]);
    const [tecnicosSeleccionados, setTecnicosSeleccionados] = useState([]);
    const [tiposFiltro, setTiposFiltro] = useState([]);
    const [statusFiltro, setStatusFiltro] = useState([]);
    const [agentesFiltro, setAgentesFiltro] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [filtroAbierto, setFiltroAbierto] = useState(false);
    const [sucursales, setSucursales] = useState([]);
    const [tareasFiltradas, setTareasFiltradas] = useState([]);
    const [gruposOriginales, setGruposOriginales] = useState([]);
    const [loading, setLoading] = useState(true);

    const [vistaActiva, setVistaActiva] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768) ? 'dia' : 'semana');
    const [toast, setToast] = useState(null);
    const [tarjetaParaMover, setTarjetaParaMover] = useState(null); // { item, modo: 'mover'|'extender' }
    const [diaPickerState, setDiaPickerState] = useState(null); // { modo: 'mover'|'eliminar'|'extender', itemFusionado }
    const ganttRef = useRef(null);

    // Colores según tipo de servicio
    const coloresTipoServicio = {
        "Instalación y Capacitación": "#90b7ff",
        "Mantenimiento Correctivo": "#ffa726",
        "Servicio Pagado": "#7ac97a",
        "Servicio en Garantía": "#e57373",
        "Vacaciones": "#ffe082",
        "Trabajo interno": "#ab47bc",
        "Día Feriado": "#38bdf8",
        "SIN_TIPO": "#9e9e9e"
    };

    const obtenerColorTipoServicio = (tipoServicio) => {
        return coloresTipoServicio[tipoServicio] || "#C2C2C2";
    };

    // Colores por sucursal
    const coloresSucursal = {
        "CDMX": "#c0392b",
        "Españoles": "#27ae60",
        "Luis Molina": "#27ae60",
        "Monterrey": "#f1c40f",
        "Ocotlán": "#7d3c98",
        "Guanajuato": "#2980b9"
    };
    const obtenerColorSucursal = (sucursal) => coloresSucursal[sucursal] || "#7f8c8d";

    const iconoSucursal = {
        "CDMX": "🦅",
        "Españoles": "🏭",
        "Luis Molina": "🏭",
        "Monterrey": "⛰️",
        "Ocotlán": "🐃",
        "Guanajuato": "👑"
    };
    const obtenerIconoSucursal = (sucursal) => iconoSucursal[sucursal] || "📍";

    const [reloadKey, setReloadKey] = useState(0);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

    const handleCloseDetails = () => {
        setOpenModal(false);
        setTareaSeleccionada(null);
        setReservaCompleta(null);
    };

    // Obtener técnicos y tareas
    useEffect(() => {
        const fetchData = async () => {
            try {
                const responseTecnicos = await fetch("https://desarrollotecnologicoar.com/api5/tecnicos?estatus=Activo&puesto=Asesor%20T%C3%A9cnico");
                const allTecnicos = await responseTecnicos.json();
                const dataTecnicos = allTecnicos;

                const sucursalesUnicas = ["TODAS", ...new Set(dataTecnicos.map(t => t.sucursal))];
                setSucursales(sucursalesUnicas);

                const gruposFormateados = dataTecnicos.map(tecnico => ({
                    id: tecnico.id,
                    title: tecnico.nombre_tecnico,
                    correo: tecnico.correo_tecnico,
                    sucursal: tecnico.sucursal
                }));
                setGrupos(gruposFormateados);
                setGruposOriginales(gruposFormateados);

                // Un solo fetch global en vez de N fetches por técnico
                const rango = API_RANGO_POR_VISTA[vistaActiva] || 'semana';
                const resGantt = await fetch(`https://desarrollotecnologicoar.com/api5/gantt_todos?rango=${encodeURIComponent(rango)}`);
                const allItems = await resGantt.json();

                // Mapear correo_tecnico → grupo id
                const correoAGrupo = {};
                dataTecnicos.forEach(t => { correoAGrupo[t.correo_tecnico] = t.id; });

                const todasLasTareas = allItems.map((rawItem, index) => {
                    const item = { ...rawItem, tipo_servicio: normalizarTipoEvento(rawItem.tipo_servicio), status_ganttic: normalizarTipoEvento(rawItem.status_ganttic) };
                    const grupoId = correoAGrupo[item.correo_tecnico];
                    if (!grupoId) return null;
                    const fechaString = item.fecha_itinerario?.split("T")[0];
                    if (!fechaString) return null;
                    const entrada = item.entrada_normalizada || item.entrada || "08:00:00";
                    const salida = item.salida_normalizada || item.salida || "18:00:00";
                    const entradaReal = item.entrada || "08:00:00";
                    const salidaReal = item.salida || "18:00:00";
                    const startDate = new Date(`${fechaString}T${entrada}`);
                    const endDate = new Date(`${fechaString}T${salida}`);
                    const startDateReal = new Date(`${fechaString}T${entradaReal}`);
                    const endDateReal = new Date(`${fechaString}T${salidaReal}`);
                    return {
                        id: `${grupoId}-${index}`,
                        group: grupoId,
                        title: item.empresa || "Sin empresa",
                        start_time: startDate,
                        start_time_real: startDateReal,
                        end_time: endDate,
                        end_time_real: endDateReal,
                        canMove: !esEventoInterno(item.tipo_servicio) && item.dia_tiene_reporte == null,
                        canResize: !esEventoInterno(item.tipo_servicio) && item.dia_tiene_reporte == null ? "right" : false,
                        reservaDetalles: item
                    };
                }).filter(Boolean);

                // ── Fusionar días consecutivos de la misma reserva y técnico ──────
                const fusionMap = new Map();
                todasLasTareas.forEach(item => {
                    const reservaId = item.reservaDetalles?.reservas_id;
                    const key = `${item.group}-${reservaId}`;
                    if (!fusionMap.has(key)) fusionMap.set(key, []);
                    fusionMap.get(key).push(item);
                });

                const tareasFusionadas = [];
                fusionMap.forEach((items) => {
                    // Ordenar por fecha de inicio
                    const sorted = [...items].sort((a, b) => a.start_time - b.start_time);

                    // Dividir en bloques de días realmente consecutivos (sin huecos)
                    const bloques = [];
                    let bloque = [sorted[0]];
                    for (let i = 1; i < sorted.length; i++) {
                        const prev = sorted[i - 1].start_time;
                        const curr = sorted[i].start_time;
                        const prevDay = new Date(new Date(prev).toISOString().split('T')[0]).getTime();
                        const currDay = new Date(new Date(curr).toISOString().split('T')[0]).getTime();
                        const diffDias = Math.round((currDay - prevDay) / DAY_MS);
                        if (diffDias === 1) {
                            bloque.push(sorted[i]);
                        } else {
                            bloques.push(bloque);
                            bloque = [sorted[i]];
                        }
                    }
                    bloques.push(bloque);

                    // Cada bloque: si tiene >1 día se fusiona, si no va suelto
                    bloques.forEach((b, bi) => {
                        if (b.length === 1) {
                            tareasFusionadas.push(b[0]);
                            return;
                        }
                        tareasFusionadas.push({
                            ...b[0],
                            id: `${b[0].id}-bloque${bi}`,
                            start_time: b[0].start_time,
                            start_time_real: b[0].start_time_real,
                            end_time: b[b.length - 1].end_time,
                            end_time_real: b[b.length - 1].end_time_real,
                            canMove: false,
                            canResize: false,
                            _diasFusionados: b,
                            _esFusion: true,
                        });
                    });
                });
                // ─────────────────────────────────────────────────────────────────

                setTareas(tareasFusionadas);
                setTareasFiltradas(tareasFusionadas);
                setUltimaActualizacion(new Date());
                setLoading(false);
            } catch (error) {
                console.error("Error al cargar datos del Gantt:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, [vistaActiva, reloadKey]);

    // Poll silencioso cada 5 minutos si la pestaña está visible
    useEffect(() => {
        const INTERVALO = 5 * 60 * 1000;
        const tick = () => {
            if (document.visibilityState === 'visible') {
                setReloadKey(k => k + 1);
            }
        };
        const id = setInterval(tick, INTERVALO);
        return () => clearInterval(id);
    }, []);

    // Cerrar modo mover con Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') setTarjetaParaMover(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    const mostrarToast = (msg, tipo = "error") => {
        setToast({ msg, tipo });
        setTimeout(() => setToast(null), 4000);
    };

    const handleEliminarDia = async (item) => {
        const idItinerario = item.reservaDetalles?.id_itinerario;
        if (!idItinerario) return;
        if (!window.confirm(`¿Eliminar el día ${new Date(item.start_time).toLocaleDateString('es-MX')} de "${item.title}"?`)) return;

        try {
            const res = await fetch(`https://desarrollotecnologicoar.com/api5/itinerario/${idItinerario}`, {
                method: "DELETE"
            });
            const data = await res.json();
            if (!res.ok) {
                mostrarToast(data.error || "Error al eliminar el día");
            } else {
                mostrarToast("Día eliminado correctamente", "ok");
                setTareas(prev => prev.filter(t => t.id !== item.id));
            }
        } catch {
            mostrarToast("Error de conexión al eliminar");
        }
    };

    const handleCanvasClick = async (groupId, time) => {
        if (!tarjetaParaMover) return;
        const { item, modo } = tarjetaParaMover;
        const nuevaFecha = new Date(time).toISOString().split("T")[0];
        const idItinerario = item.reservaDetalles?.id_itinerario;
        const entrada = item.reservaDetalles?.entrada || "08:00:00";
        const salida = item.reservaDetalles?.salida || "18:00:00";

        setTarjetaParaMover(null);

        if (modo === 'mover') {
            const fechaActual = new Date(item.start_time).toISOString().split("T")[0];
            if (nuevaFecha === fechaActual) return;

            const prevTareas = tareas;

            const normalizarFecha = (prev, fecha, grupoId, itemMovido) => {
                const pad = n => String(n).padStart(2, "0");
                const enFecha = prev.filter(t =>
                    t.group === grupoId &&
                    t.id !== itemMovido.id &&
                    new Date(t.start_time).toISOString().split("T")[0] === fecha
                );
                if (itemMovido && fecha === nuevaFecha) {
                    // Include the moved item at destination
                    const todos = [...enFecha, itemMovido];
                    const total = todos.length;
                    return todos.map((t, i) => {
                        let startH, startM, endH, endM;
                        if (total === 1) {
                            startH = 0; startM = 1; endH = 23; endM = 59;
                        } else {
                            const minutosDia = 24 * 60;
                            const bloque = Math.floor(minutosDia / total);
                            const inicioMin = i * bloque;
                            const finMin = i === total - 1 ? minutosDia - 1 : (i + 1) * bloque;
                            startH = Math.floor(inicioMin / 60); startM = inicioMin % 60;
                            endH = Math.floor(finMin / 60); endM = finMin % 60;
                        }
                        const s = new Date(`${fecha}T${pad(startH)}:${pad(startM)}:00`);
                        const e = new Date(`${fecha}T${pad(endH)}:${pad(endM)}:00`);
                        return { ...t, start_time: s, end_time: e };
                    });
                } else {
                    // Re-normalize source date (item removed)
                    const total = enFecha.length;
                    if (total === 0) return [];
                    return enFecha.map((t, i) => {
                        let startH, startM, endH, endM;
                        if (total === 1) {
                            startH = 0; startM = 1; endH = 23; endM = 59;
                        } else {
                            const minutosDia = 24 * 60;
                            const bloque = Math.floor(minutosDia / total);
                            const inicioMin = i * bloque;
                            const finMin = i === total - 1 ? minutosDia - 1 : (i + 1) * bloque;
                            startH = Math.floor(inicioMin / 60); startM = inicioMin % 60;
                            endH = Math.floor(finMin / 60); endM = finMin % 60;
                        }
                        const s = new Date(`${fecha}T${pad(startH)}:${pad(startM)}:00`);
                        const e = new Date(`${fecha}T${pad(endH)}:${pad(endM)}:00`);
                        return { ...t, start_time: s, end_time: e };
                    });
                }
            };

            setTareas(prev => {
                const idsOrigen = new Set(
                    prev.filter(t =>
                        t.group === item.group &&
                        new Date(t.start_time).toISOString().split("T")[0] === fechaActual
                    ).map(t => t.id)
                );
                const idsDestino = new Set(
                    prev.filter(t =>
                        t.group === item.group &&
                        t.id !== item.id &&
                        new Date(t.start_time).toISOString().split("T")[0] === nuevaFecha
                    ).map(t => t.id)
                );
                const normOrigen = normalizarFecha(prev, fechaActual, item.group, item);
                const normDestino = normalizarFecha(prev, nuevaFecha, item.group, item);
                const idsTocados = new Set([...idsOrigen, ...idsDestino, item.id]);
                return [
                    ...prev.filter(t => !idsTocados.has(t.id)),
                    ...normOrigen,
                    ...normDestino
                ];
            });

            try {
                const res = await fetch(`https://desarrollotecnologicoar.com/api5/itinerario/${idItinerario}/mover`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ nueva_fecha: nuevaFecha })
                });
                const data = await res.json();
                if (!res.ok) {
                    mostrarToast(data.error || "Error al mover el día");
                    setTareas(prevTareas);
                } else {
                    mostrarToast(`Movido al ${nuevaFecha}`, "ok");
                }
            } catch {
                mostrarToast("Error de conexión");
                setTareas(prevTareas);
            }
        } else if (modo === 'extender') {
            const fechaActual = new Date(item.start_time).toISOString().split("T")[0];
            if (nuevaFecha === fechaActual) return;
            const reservaId = item.reservaDetalles?.reservas_id;

            try {
                const res = await fetch("https://desarrollotecnologicoar.com/api5/itinerario/extender_dia", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ reserva_id: reservaId, nueva_fecha: nuevaFecha, entrada, salida })
                });
                const data = await res.json();
                if (!res.ok) {
                    mostrarToast(data.error || "Error al agregar día");
                } else {
                    mostrarToast(`Nuevo día agregado: ${nuevaFecha}`, "ok");
                    const nuevoItem = {
                        id: `ext-${data.itinerario.id}`,
                        group: item.group,
                        title: item.title,
                        start_time: null, // se calcula en normalización
                        end_time: null,
                        reservaDetalles: {
                            ...item.reservaDetalles,
                            id_itinerario: data.itinerario.id,
                            dia_tiene_reporte: null,
                            fecha_itinerario: nuevaFecha
                        }
                    };

                    setTareas(prev => {
                        // Todos los items del mismo técnico en esa fecha nueva
                        const enMismaFecha = prev.filter(t =>
                            t.group === item.group &&
                            new Date(t.start_time).toISOString().split("T")[0] === nuevaFecha
                        );
                        const todos = [...enMismaFecha, nuevoItem];
                        const total = todos.length;

                        const normalizados = todos.map((t, i) => {
                            let startH, startM, endH, endM;
                            if (total === 1) {
                                startH = 0; startM = 1; endH = 23; endM = 59;
                            } else {
                                const minutosDia = 24 * 60;
                                const bloque = Math.floor(minutosDia / total);
                                const inicioMin = i * bloque;
                                const finMin = i === total - 1 ? minutosDia - 1 : (i + 1) * bloque;
                                startH = Math.floor(inicioMin / 60); startM = inicioMin % 60;
                                endH = Math.floor(finMin / 60); endM = finMin % 60;
                            }
                            const pad = n => String(n).padStart(2, "0");
                            const s = new Date(`${nuevaFecha}T${pad(startH)}:${pad(startM)}:00`);
                            const e = new Date(`${nuevaFecha}T${pad(endH)}:${pad(endM)}:00`);
                            return { ...t, start_time: s, end_time: e };
                        });

                        // Reemplazar los existentes y agregar el nuevo
                        const idsActualizados = new Set(enMismaFecha.map(t => t.id));
                        return [
                            ...prev.filter(t => !idsActualizados.has(t.id)),
                            ...normalizados
                        ];
                    });
                }
            } catch {
                mostrarToast("Error de conexión");
            }
        }
    };

    const navegarIzquierda = () => {
        if (vistaActiva === 'dia') {
            setVisibleTimeStart(v => v - DAY_MS);
            setVisibleTimeEnd(v => v - DAY_MS);
        } else {
            const span = visibleTimeEnd - visibleTimeStart;
            setVisibleTimeStart(v => v - span / 3);
            setVisibleTimeEnd(v => v - span / 3);
        }
    };

    const navegarDerecha = () => {
        if (vistaActiva === 'dia') {
            setVisibleTimeStart(v => v + DAY_MS);
            setVisibleTimeEnd(v => v + DAY_MS);
        } else {
            const span = visibleTimeEnd - visibleTimeStart;
            setVisibleTimeStart(v => v + span / 3);
            setVisibleTimeEnd(v => v + span / 3);
        }
    };

    const handleItemMove = async (itemId, dragTime, newGroupOrder) => { };
    const handleItemResize = async (itemId, time, edge) => { };

    const toggleChip = (setter) => (valor) =>
        setter(prev => prev.includes(valor) ? prev.filter(v => v !== valor) : [...prev, valor]);

    const handleTecnicoToggle = toggleChip(setTecnicosSeleccionados);

    const handleAgenteToggle = toggleChip(setAgentesFiltro);

    const limpiarTodosFiltros = () => {
        setSucursalesFiltro([]);
        setTecnicosSeleccionados([]);
        setTiposFiltro([]);
        setStatusFiltro([]);
        setAgentesFiltro([]);
    };

    const totalFiltrosActivos = sucursalesFiltro.length + tecnicosSeleccionados.length + tiposFiltro.length + statusFiltro.length + agentesFiltro.length;

    useEffect(() => {
        const gruposFiltrados = gruposOriginales.filter(g => {
            const filtroSucursal = sucursalesFiltro.length === 0 || sucursalesFiltro.includes(g.sucursal);
            const filtroNombre = tecnicosSeleccionados.length === 0 || tecnicosSeleccionados.includes(g.title);
            return filtroSucursal && filtroNombre;
        });

        setGrupos(gruposFiltrados);

        const idsVisibles = new Set(gruposFiltrados.map(g => g.id));
        const nuevasTareas = tareas.filter(t => {
            if (!idsVisibles.has(t.group)) return false;
            if (tiposFiltro.length > 0) {
                const tipo = t.reservaDetalles?.tipo_servicio;
                const tipoKey = !tipo || tipo === "N/A" ? "SIN_TIPO" : tipo;
                if (!tiposFiltro.includes(tipoKey)) return false;
            }
            if (statusFiltro.length > 0) {
                const status = t.reservaDetalles?.status_ganttic;
                if (!statusFiltro.includes(status)) return false;
            }
            if (agentesFiltro.length > 0) {
                const agente = t.reservaDetalles?.nombre_agente || "";
                if (!agentesFiltro.includes(agente)) return false;
            }
            if (busqueda.trim()) {
                const q = busqueda.toLowerCase();
                const empresa = (t.title || "").toLowerCase();
                const ticket = String(t.reservaDetalles?.ticket || "").toLowerCase();
                if (!empresa.includes(q) && !ticket.includes(q)) return false;
            }
            return true;
        });
        setTareasFiltradas(nuevasTareas);
    }, [sucursalesFiltro, tecnicosSeleccionados, tiposFiltro, statusFiltro, agentesFiltro, busqueda, gruposOriginales, tareas]);

    const handleItemClick = async (itemId) => {
        // Buscar primero directo; si no, buscar dentro de _diasFusionados (vista día expande ítems fusionados)
        let tarea = tareas.find(t => t.id === itemId);
        if (!tarea) {
            for (const t of tareas) {
                if (t._esFusion && t._diasFusionados) {
                    const diaItem = t._diasFusionados.find(d => d.id === itemId);
                    if (diaItem) { tarea = diaItem; break; }
                }
            }
        }
        setTareaSeleccionada(tarea);
        setReservaCompleta(null);
        setOpenModal(true);

        const rid = tarea?.reservaDetalles?.reservas_id;
        if (!rid) return;

        try {
            const res = await fetch(`https://desarrollotecnologicoar.com/api5/reserva_id/${rid}`);
            const data = await res.json();
            setReservaCompleta(data);
            setItinerarioEdits(
                (data.itinerario || []).map(it => ({
                    id: it.id,
                    fecha: it.fecha,
                    entrada: it.entrada || "",
                    salida: it.salida || "",
                    id_reporte: it.id_reporte,
                    modificado: false,
                }))
            );
        } catch (error) {
            console.error("Error cargando reserva:", error);
        }
    };

    const handleGuardarHorario = async (itEdit) => {
        setGuardandoHorario(itEdit.id);
        try {
            const res = await fetch(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${itEdit.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    reserva_id: tareaSeleccionada?.reservaDetalles?.reservas_id,
                    fecha: itEdit.fecha,
                    entrada: itEdit.entrada,
                    salida: itEdit.salida,
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                mostrarToast(err.error || "Error al guardar horario");
            } else {
                setItinerarioEdits(prev =>
                    prev.map(e => e.id === itEdit.id ? { ...e, modificado: false } : e)
                );
                mostrarToast("Horario actualizado", "ok");
            }
        } catch {
            mostrarToast("Error de conexión al guardar horario");
        } finally {
            setGuardandoHorario(null);
        }
    };

    // Iconos por status ganttic
    const iconoPorStatusGanttic = (status) => {
        const sz = density === 'ultra' ? 'w-[9px] h-[9px]' : density === 'compact' ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]';
        switch (status) {
            case "Servicio en progreso":
                return <HiClock className={`text-red-200 ${sz}`} />;
            case "Pendiente de empezar el servicio":
                return <HiCalendarDays className={`text-amber-200 ${sz}`} />;
            case "Servicio realizado":
            case "Servicio finalizado":
                return <HiCheckBadge className={`text-blue-200 ${sz}`} />;
            case "Servicio aprobado pendiente hoja":
                return <HiExclamationTriangle className={`text-amber-200 ${sz}`} />;
            case "Servicio apartado":
                return <HiNoSymbol className={`text-white/60 ${sz}`} />;
            case "Pendiente de clasificación":
                return <HiQuestionMarkCircle className={`text-white/50 ${sz}`} />;
            default:
                return <HiQuestionMarkCircle className={`text-white/50 ${sz}`} />;
        }
    };

    const parsearCiudadEstado = (domicilio = "") => {
        const partes = domicilio.split(",").map(p => p.trim());
        const estado = partes.pop() || "";
        const ciudad = partes.pop() || "";
        return { ciudad: ciudad.toUpperCase(), estado: estado.toUpperCase() };
    };

    const irASemanaActual = () => {
        const hoy = startOfDay(new Date());
        const diaSemana = hoy.getDay(); // 0=Dom, 1=Lun...
        const lunes = new Date(hoy);
        lunes.setDate(hoy.getDate() - (diaSemana === 0 ? 6 : diaSemana - 1));
        setVisibleTimeStart(lunes.getTime());
        setVisibleTimeEnd(lunes.getTime() + 7 * DAY_MS);
    };

    const irADiaActual = () => {
        const hoy = startOfDay(new Date()).getTime();
        setVisibleTimeStart(hoy + 7 * 60 * 60 * 1000);   // 7:00 am
        setVisibleTimeEnd(hoy + 21 * 60 * 60 * 1000);    // 9:00 pm
    };

    const irAMesActual = () => {
        const hoy = new Date();
        const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const primerDiaSiguiente = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
        setVisibleTimeStart(primerDia.getTime());
        setVisibleTimeEnd(primerDiaSiguiente.getTime());
    };

    const irAMesAnterior = () => {
        const hoy = new Date();
        const primerDiaMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
        const primerDiaMesActual = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        setVisibleTimeStart(primerDiaMesAnterior.getTime());
        setVisibleTimeEnd(primerDiaMesActual.getTime());
    };

    const cambiarVista = (nuevaVista) => {
        setVistaActiva(nuevaVista);
        switch (nuevaVista) {
            case 'dia': irADiaActual(); break;
            case 'semana': irASemanaActual(); break;
            case 'mes': irAMesActual(); break;
            case 'mes_anterior': irAMesAnterior(); break;
            default: irASemanaActual();
        }
    };

    const handleDensityChange = (newDensity) => {
        setDensity(newDensity);
        try { localStorage.setItem('gantt_density', newDensity); } catch { }
    };

    const handleTimeChange = (start, end, updateScrollCanvas) => {
        if (isMobile) {
            // En móvil bloqueamos el paneo táctil; la navegación es solo con las flechas
            updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
            return;
        }
        setVisibleTimeStart(start);
        setVisibleTimeEnd(end);
        updateScrollCanvas(start, end);
    };

    // CSS overrides for react-calendar-timeline item borders
    const timelineContainerStyle = `
        .gantt-container {
            border: 1px solid #e4e4e7;
            border-radius: 16px;
            overflow: clip;
            background: #ffffff;
        }
        .gantt-container .rct-header-root {
            background: #fafafa;
            border-bottom: 1px solid #e4e4e7;
            position: sticky;
            top: 0;
            z-index: 30;
        }
        .gantt-container .rct-dateHeader {
            color: #3f3f46;
            font-weight: 600;
            font-size: 11px;
            border-left: 1px solid #e9ecef;
            border-bottom: 1px solid #e9ecef;
            background: #fafafa;
        }
        .gantt-container .rct-sidebar {
            background: #fcfcfc;
            border-right: 1px solid #e5e7eb;
        }
        .gantt-container .rct-horizontal-lines .rct-hl-even,
        .gantt-container .rct-horizontal-lines .rct-hl-odd {
            border-bottom: 1px solid #f4f4f5;
            background: #ffffff;
        }
        .gantt-container .rct-vertical-lines .rct-vl {
            border-left: 1px solid #eeeeee;
            z-index: 0 !important;
        }
        .gantt-container .rct-items {
            z-index: 2 !important;
        }
        .gantt-container .rct-scroll {
            cursor: default !important;
        }
        .gantt-scroll-area {
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            border-radius: 16px;
            min-height: 0;
        }
        .gantt-container .rct-item:hover,
        .gantt-container .rct-item,
        .gantt-container .rct-item.selected,
        .gantt-container .rct-item.rct-item-selected {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
            z-index: 1 !important;
        }
        .gantt-container .rct-item.selected,
        .gantt-container .rct-item.rct-item-selected {
            background-color: inherit !important;
            filter: none !important;
            outline: none !important;
            z-index: 2 !important;
        }
        @keyframes ganttDrawerSlideIn {
            from {
                transform: translateX(100%);
            }
            to {
                transform: translateX(0);
            }
        }
        .gantt-detail-drawer {
            animation: ganttDrawerSlideIn 0.24s ease-out;
        }
        .rct-today-marker {
            background: rgba(239, 68, 68, 0.7) !important;
            width: 2px !important;
            z-index: 5 !important;
        }
        .weekend-col {
            background: rgba(241, 245, 249, 0.75) !important;
        }
        .gantt-container .rct-sidebar .rct-sidebar-row {
            padding: 0 !important;
        }
        .gantt-container .rct-item.gantt-item-fusion {
            box-shadow: 0 3px 10px rgba(0,0,0,0.22) !important;
            transform: translateY(-3px);
            z-index: 5 !important;
            border-radius: 6px !important;
        }
    `;

    return (
        <div className="page-container bg-zinc-50/60 rounded-3xl flex flex-col" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
            <style>{timelineContainerStyle}</style>

            {/* BANNER MODO MOVER */}
            {tarjetaParaMover && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[2000] flex items-center gap-3 px-5 py-3 rounded-2xl bg-zinc-900 text-white shadow-2xl text-sm font-medium">
                    <span>{tarjetaParaMover.modo === 'mover' ? '↔ Haz clic en el día destino para mover' : '+ Haz clic en el día donde agregar'}</span>
                    <span className="text-zinc-400">|</span>
                    <span className="font-bold truncate max-w-[180px]">{tarjetaParaMover.item.title}</span>
                    <button onClick={() => setTarjetaParaMover(null)} className="ml-1 text-zinc-400 hover:text-white transition-colors text-base leading-none">✕</button>
                </div>
            )}

            {/* TOAST */}
            {toast && (
                <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[2000] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${toast.tipo === "ok" ? "bg-green-600 text-white" : "bg-red-600 text-white"
                    }`}>
                    {toast.tipo === "ok" ? "✓" : "✕"} {toast.msg}
                </div>
            )}

            {/* DIA PICKER — selector de día para ítems fusionados */}
            {diaPickerState && (
                <>
                    <div className="fixed inset-0 z-[1900]" onClick={() => setDiaPickerState(null)} />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2000] bg-white rounded-2xl shadow-2xl border border-zinc-200 p-5 w-72">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-bold text-zinc-900">
                                {diaPickerState.modo === 'mover' && '↔ ¿Qué día mover?'}
                                {diaPickerState.modo === 'eliminar' && '🗑 ¿Qué día eliminar?'}
                                {diaPickerState.modo === 'extender' && '+ ¿Desde qué día extender?'}
                            </span>
                            <button onClick={() => setDiaPickerState(null)} className="text-zinc-400 hover:text-zinc-700 text-lg leading-none">✕</button>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            {diaPickerState.itemFusionado._diasFusionados.map((diaItem) => {
                                const fecha = diaItem.reservaDetalles?.fecha_itinerario?.split('T')[0] || '';
                                const fechaLabel = fecha
                                    ? format(parseISO(fecha), "EEEE d 'de' MMMM", { locale: es })
                                    : 'Sin fecha';
                                const tieneReporte = diaItem.reservaDetalles?.dia_tiene_reporte != null;
                                const deshabilitado = diaPickerState.modo !== 'extender' && tieneReporte;
                                return (
                                    <button
                                        key={diaItem.id}
                                        disabled={deshabilitado}
                                        onClick={() => {
                                            setDiaPickerState(null);
                                            const modo = diaPickerState.modo;
                                            if (modo === 'eliminar') {
                                                handleEliminarDia(diaItem);
                                            } else if (modo === 'mover') {
                                                setTarjetaParaMover({ item: diaItem, modo: 'mover' });
                                            } else if (modo === 'extender') {
                                                setTarjetaParaMover({ item: diaItem, modo: 'extender' });
                                            }
                                        }}
                                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors text-left
                                            ${deshabilitado
                                                ? 'border-zinc-100 bg-zinc-50 text-zinc-300 cursor-not-allowed'
                                                : 'border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 cursor-pointer'}`}
                                    >
                                        <span className="capitalize">{fechaLabel}</span>
                                        {tieneReporte && <span className="text-[10px] text-zinc-400 ml-2">con reporte</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* ENCABEZADO + CONTROLES */}
            <div className="relative mb-2 flex-shrink-0">
                <div className="flex items-center gap-1.5 flex-nowrap w-full overflow-x-auto pb-0.5">

                    {/* Nombre de producto */}
                    {!isMobile && (
                        <div className="flex items-center gap-1.5 flex-shrink-0 mr-1 select-none">
                            <span className="inline-flex items-center gap-1 bg-zinc-900 text-white text-[10px] font-black tracking-widest px-2 py-1 rounded-md uppercase leading-none">
                                <span style={{ color: '#f59e0b' }}>▸</span>GANTT
                            </span>
                            <span className="text-[10px] font-semibold text-zinc-400 whitespace-nowrap hidden lg:block">Itinerarios · AR Tecnología</span>
                        </div>
                    )}

                    {/* Divisor */}
                    <div className="w-px h-4 bg-zinc-200 flex-shrink-0" />

                    {/* Recargar */}
                    <button type="button" onClick={() => { setLoading(true); setReloadKey(k => k + 1); }} title="Recargar datos"
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600 text-[11px] font-semibold transition-colors flex-shrink-0">
                        <HiArrowPath className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                        Recargar
                    </button>

                    {/* Última actualización */}
                    {ultimaActualizacion && (
                        <span className="text-[10px] text-zinc-400 flex-shrink-0 whitespace-nowrap hidden md:block">
                            {ultimaActualizacion.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}

                    {/* Spacer — empuja lo siguiente al extremo derecho */}
                    <div className="flex-1" />

                    {/* Buscar */}
                    <div className="relative flex-shrink-0">
                        <HiMagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400 pointer-events-none" />
                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar..."
                            className={`input h-7 pl-6 pr-2 text-[11px] border-zinc-300 focus:border-zinc-700 focus:ring-zinc-700/10 rounded-md ${isMobile ? 'w-36' : 'w-52'}`}
                        />
                    </div>

                    {/* Abrir reserva — solo mobile */}
                    {isMobile && (
                        <button
                            type="button"
                            onClick={() => {
                                if (!busqueda.trim()) return;
                                const q = busqueda.toLowerCase();
                                const match = tareasFiltradas.find(t =>
                                    String(t.reservaDetalles?.ticket || '').toLowerCase().includes(q) ||
                                    (t.title || '').toLowerCase().includes(q)
                                );
                                if (match) handleItemClick(match.id);
                                else mostrarToast('No se encontró ningún resultado');
                            }}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-zinc-700 bg-zinc-900 text-white text-[11px] font-semibold whitespace-nowrap flex-shrink-0 active:opacity-80"
                        >
                            Abrir
                        </button>
                    )}

                    {/* Filtrar */}
                    <button type="button" onClick={() => setFiltroAbierto(prev => !prev)}
                        className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${filtroAbierto || totalFiltrosActivos > 0 ? "border-red-500 text-red-600 bg-red-50" : "border-zinc-300 text-zinc-600 bg-white hover:bg-zinc-50"}`}>
                        <HiFunnel className="w-3 h-3" />
                        Filtrar
                        {totalFiltrosActivos > 0 && <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold">{totalFiltrosActivos}</span>}
                    </button>

                    {/* Divisor */}
                    <div className="w-px h-4 bg-zinc-200 flex-shrink-0" />

                    {/* Densidad — segmented pill */}
                    <div className="inline-flex items-center rounded-md border border-zinc-300 bg-white overflow-hidden flex-shrink-0 h-7" title="Densidad de filas">
                        {[{ val: "comfortable", label: "C", tip: "Cómodo" }, { val: "compact", label: "M", tip: "Compacto" }, { val: "ultra", label: "U", tip: "Máxima" }].map(({ val, label, tip }) => (
                            <button key={val} type="button" title={tip} onClick={() => handleDensityChange(val)}
                                className={`px-2 h-full text-[11px] font-bold border-r border-zinc-200 last:border-r-0 transition-colors ${density === val ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-50"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Divisor */}
                    <div className="w-px h-4 bg-zinc-200 flex-shrink-0" />

                    {/* Nav ← vista → */}
                    <div className="inline-flex items-center rounded-md border border-zinc-300 bg-white overflow-hidden flex-shrink-0 h-7">
                        {/* ← */}
                        <button type="button" onClick={navegarIzquierda} title="Retroceder"
                            className="px-1.5 h-full border-r border-zinc-200 text-zinc-500 hover:bg-zinc-50 transition-colors flex items-center">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        {/* Vistas */}
                        {[{ id: "dia", label: "Día" }, { id: "semana", label: "Sem" }, { id: "mes", label: "Mes" }, { id: "mes_anterior", label: "Ant" }]
                            .filter(({ id }) => !isMobile || id === 'dia')
                            .map(({ id, label }) => (
                            <button key={id} type="button" onClick={() => cambiarVista(id)} title={id === 'mes_anterior' ? 'Mes anterior' : undefined}
                                className={`px-2 h-full text-[11px] font-semibold border-r border-zinc-200 last:border-r-0 whitespace-nowrap transition-colors ${vistaActiva === id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
                                {label}
                            </button>
                        ))}
                        {/* → */}
                        <button type="button" onClick={navegarDerecha} title="Avanzar"
                            className="px-1.5 h-full text-zinc-500 hover:bg-zinc-50 transition-colors flex items-center">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>

                {/* Filter Panel */}
                {filtroAbierto && (
                    <>
                        <div className="fixed inset-0 z-[90]" onClick={() => setFiltroAbierto(false)} />
                        <div className="absolute right-0 top-[calc(100%+8px)] z-[100] w-80 bg-white border border-zinc-200 rounded-2xl shadow-xl p-4 space-y-5">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-zinc-900">Filtros</span>
                                {totalFiltrosActivos > 0 && (
                                    <button onClick={limpiarTodosFiltros} className="text-xs font-medium text-red-600 hover:underline">Limpiar todo</button>
                                )}
                            </div>

                            {/* SIMBOLOGÍA */}
                            <div>
                                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Simbología</p>
                                <div className="flex flex-wrap gap-1">
                                    {[
                                        { valor: "Servicio en progreso", label: "En progreso", icon: <HiClock className="w-3 h-3 text-red-500" /> },
                                        { valor: "Pendiente de empezar el servicio", label: "Pendiente", icon: <HiCalendarDays className="w-3 h-3 text-amber-500" /> },
                                        { valor: "Servicio realizado", label: "Realizado", icon: <HiCheckBadge className="w-3 h-3 text-blue-500" /> },
                                        { valor: "Servicio finalizado", label: "Finalizado", icon: <HiCheckBadge className="w-3 h-3 text-green-500" /> },
                                        { valor: "Servicio apartado", label: "Apartado", icon: <HiNoSymbol className="w-3 h-3 text-yellow-500" /> },
                                    ].map(({ valor, label, icon }) => {
                                        const active = statusFiltro.includes(valor);
                                        return (
                                            <button key={valor} onClick={() => toggleChip(setStatusFiltro)(valor)}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                                                    }`}>
                                                {icon}{label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* TIPO DE SERVICIO */}
                            <div>
                                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Tipo de servicio</p>
                                <div className="flex flex-wrap gap-1">
                                    {[
                                        { valor: "Instalación y Capacitación", color: "#90b7ff" },
                                        { valor: "Mantenimiento Correctivo", color: "#ffa726" },
                                        { valor: "Servicio Pagado", color: "#7ac97a" },
                                        { valor: "Servicio en Garantía", color: "#e57373" },
                                        { valor: "Vacaciones", color: "#ffe082" },
                                        { valor: "Trabajo interno", color: "#ab47bc" },
                                        { valor: "Día Feriado", color: "#38bdf8" },
                                    ].map(({ valor, color }) => {
                                        const active = tiposFiltro.includes(valor);
                                        return (
                                            <button key={valor} onClick={() => toggleChip(setTiposFiltro)(valor)}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                                                    }`}>
                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                                {valor}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* SUCURSAL */}
                            <div>
                                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Sucursal</p>
                                <div className="flex flex-wrap gap-1">
                                    {sucursales.filter(s => s !== "TODAS").map(s => {
                                        const active = sucursalesFiltro.includes(s);
                                        return (
                                            <button key={s} onClick={() => toggleChip(setSucursalesFiltro)(s)}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                                                    }`}>
                                                <span>{obtenerIconoSucursal(s)}</span>{s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* AGENTE */}
                            <div>
                                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Solicitado por</p>
                                <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1 pr-1">
                                    {[...new Map(
                                        tareas
                                            .filter(t => t.reservaDetalles?.nombre_agente)
                                            .map(t => [t.reservaDetalles.nombre_agente, t.reservaDetalles.nombre_agente])
                                    ).values()].sort().map(nombre => {
                                        const active = agentesFiltro.includes(nombre);
                                        return (
                                            <button key={nombre} onClick={() => handleAgenteToggle(nombre)}
                                                className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                                                    }`}>
                                                {nombre}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* TÉCNICO */}
                            <div>
                                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Técnico</p>
                                <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1 pr-1">
                                    {gruposOriginales
                                        .filter(g => sucursalesFiltro.length === 0 || sucursalesFiltro.includes(g.sucursal))
                                        .map(g => {
                                            const active = tecnicosSeleccionados.includes(g.title);
                                            return (
                                                <button key={g.id} onClick={() => handleTecnicoToggle(g.title)}
                                                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                                                        }`}>
                                                    {g.title}
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {loading ? (
                <div className="flex flex-col items-center py-16 rounded-2xl border border-zinc-200 bg-white">
                    <img src="./dragon.gif" className="max-w-[240px] grayscale" alt="Cargando" />
                    <p className="text-zinc-500 mt-4">Cargando datos...</p>
                </div>
            ) : grupos.length === 0 ? (
                <div className="empty-state rounded-2xl border border-zinc-200 bg-white">
                    <p className="text-lg font-semibold text-zinc-700">No se encontraron técnicos</p>
                    <p className="text-sm text-zinc-500">Intenta con otro nombre o sucursal</p>
                </div>
            ) : (
                <div className="flex flex-col flex-1 min-h-0">
                    <div className="gantt-scroll-area flex-1" ref={ganttRef}>
                        <div className="gantt-container w-full">
                            {(() => {
                                const tareasParaTimeline = vistaActiva === 'dia'
                                    ? tareasFiltradas.flatMap(t => {
                                        if (t._esFusion) {
                                            // Expandir a días individuales con horario real
                                            return t._diasFusionados.map(d => ({
                                                ...d,
                                                start_time: d.start_time_real || d.start_time,
                                                end_time: d.end_time_real || d.end_time,
                                                _esFusion: false,
                                            }));
                                        }
                                        return [{
                                            ...t,
                                            start_time: t.start_time_real || t.start_time,
                                            end_time: t.end_time_real || t.end_time,
                                        }];
                                    })
                                    : tareasFiltradas;
                                return (
                            <Timeline
                                groups={grupos}
                                items={tareasParaTimeline}
                                visibleTimeStart={visibleTimeStart}
                                visibleTimeEnd={visibleTimeEnd}
                                onTimeChange={handleTimeChange}
                                minZoom={60 * 60 * 1000}
                                maxZoom={31 * DAY_MS}
                                canMove={false}
                                canResize={false}
                                canChangeGroup={false}
                                onItemMove={handleItemMove}
                                onItemResize={handleItemResize}
                                onCanvasClick={handleCanvasClick}
                                lineHeight={currentDensity.lineHeight}
                                itemHeightRatio={0.72}
                                sidebarWidth={isMobile ? 72 : currentDensity.sidebarW}
                                onItemSelect={() => { }}
                                onItemDeselect={() => { }}
                                selected={[]}
                                verticalLineClassNamesForTime={(timeStart) => {
                                    const day = new Date(timeStart).getDay();
                                    return (day === 0 || day === 6) ? ['weekend-col'] : [];
                                }}
                                groupRenderer={({ group }) => {
                                    const words = group.title.trim().split(' ');
                                    const initials = words.slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
                                    const sucursalColor = obtenerColorSucursal(group.sucursal);
                                    const ABREV = { 'CDMX': 'CDX', 'Españoles': 'ESP', 'Luis Molina': 'LMO', 'Monterrey': 'MTY', 'Ocotlán': 'OCO', 'Guanajuato': 'GTO', '8 de Julio': '8JL' };
                                    const sucAbrev = ABREV[group.sucursal] || group.sucursal.slice(0, 3).toUpperCase();
                                    const shortName = `${words[0] || ''} ${words[words.length - 1] || ''}`.trim();
                                    const nick = isMobile
                                        ? `${(words[0] || '').slice(0, 2)} ${(words[words.length - 1] || '').slice(0, 2)}`.trim()
                                        : shortName;
                                    return (
                                        <div className="flex items-center h-full pl-2 pr-1.5 bg-[#fafafa] border-b border-zinc-100 gap-1" title={`${group.title} · ${group.sucursal}`}>
                                            <span className="flex-shrink-0 w-[20px] h-[20px] rounded-full flex items-center justify-center text-white font-bold leading-none" style={{ background: sucursalColor, fontSize: '7px' }}>
                                                {initials}
                                            </span>
                                            <span className="text-[11px] font-semibold text-zinc-800 truncate flex-1 min-w-0">{nick}</span>
                                            {!isMobile && <span className="flex-shrink-0 text-[8px] font-bold px-1 py-px rounded" style={{ background: sucursalColor + '28', color: sucursalColor }}>{sucAbrev}</span>}
                                        </div>
                                    );
                                }}
                                itemRenderer={({ item, getItemProps }) => {
                                    const tipoRaw = item.reservaDetalles.tipo_servicio;
                                    const tipo = !tipoRaw || tipoRaw === "N/A" ? "SIN_TIPO" : tipoRaw;
                                    const statusGanttic = item.reservaDetalles.status_ganttic;
                                    const domicilio = item.reservaDetalles.domicilio || "";
                                    // Para ítems fusionados: puede mover/eliminar si AL MENOS UN día no tiene reporte
                                    const puedeMover = item._esFusion
                                        ? item._diasFusionados.some(d => d.reservaDetalles?.dia_tiene_reporte == null)
                                        : (item.reservaDetalles?.dia_tiene_reporte === null || item.reservaDetalles?.dia_tiene_reporte === undefined);
                                    // Solo puede extender si la reserva sigue a nombre del mismo técnico que aparece en la tarjeta
                                    const mismoTecnico = !item.reservaDetalles?.correo_tecnico_reserva ||
                                        item.reservaDetalles.correo_tecnico === item.reservaDetalles.correo_tecnico_reserva;
                                    const puedeExtender = item.reservaDetalles?.status_reserva !== 'Finalizado' && mismoTecnico;
                                    const estaSeleccionado = tarjetaParaMover?.item?.id === item.id;

                                    // Eventos internos: estilo y etiqueta comunes para las tres modalidades.
                                    if (esEventoInterno(tipo)) {
                                        const evento = EVENTOS_INTERNOS[tipo];
                                        return (
                                            <div
                                                {...getItemProps({
                                                    style: {
                                                        backgroundColor: evento.fondo,
                                                        borderLeft: `3px solid ${evento.borde}`,
                                                        color: evento.texto,
                                                        fontWeight: "bold",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        height: `${ALTURA_TARJETA_GANTT}px`,
                                                        borderRadius: "4px",
                                                        padding: "0 6px",
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "ellipsis",
                                                        outline: "none",
                                                        boxShadow: "none",
                                                        boxSizing: "border-box",
                                                    },
                                                    onClick: () => handleItemClick(item.id),
                                                })}
                                            >
                                                <span className="flex items-center mr-1.5 flex-shrink-0">
                                                    {tipo === "Vacaciones" ? <FaUmbrellaBeach style={{ fontSize: 11 }} /> : tipo === "Trabajo interno" ? <HiWrench style={{ fontSize: 11 }} /> : <HiCalendarDays style={{ fontSize: 11 }} />}
                                                </span>
                                                <span className="text-[10px] font-bold truncate leading-none">{tipo.toUpperCase()}</span>
                                            </div>
                                        );
                                    }

                                    // Normal items
                                    const baseColor = obtenerColorTipoServicio(tipo);
                                    const { ciudad, estado } = parsearCiudadEstado(domicilio);
                                    const STRIPE_COLORS = {
                                        "Servicio en progreso": "#dc2626",
                                        "Pendiente de empezar el servicio": "#d97706",
                                        "Servicio realizado": "#2563eb",
                                        "Servicio finalizado": "#16a34a",
                                        "Servicio aprobado pendiente hoja": "#ea580c",
                                        "Servicio apartado": "#6b7280",
                                    };
                                    const stripeColor = STRIPE_COLORS[statusGanttic] || "#6b7280";
                                    const esHistorico = item.reservaDetalles?.es_historico === true;
                                    const esFusion = item._esFusion === true;
                                    const numDias = esFusion ? item._diasFusionados.length : 1;

                                    return (
                                        <div
                                            {...getItemProps({
                                                style: {
                                                    backgroundColor: estaSeleccionado ? "#1e293b" : baseColor,
                                                    borderLeft: `3px solid ${estaSeleccionado ? 'rgba(255,255,255,0.6)' : stripeColor}`,
                                                    borderRight: esFusion ? `3px solid ${estaSeleccionado ? 'rgba(255,255,255,0.6)' : stripeColor}` : undefined,
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    height: `${ALTURA_TARJETA_GANTT}px`,
                                                    borderRadius: "4px",
                                                    padding: "0 4px 0 6px",
                                                    opacity: esHistorico ? 0.6 : 1,
                                                    overflow: "hidden",
                                                    cursor: tarjetaParaMover ? "crosshair" : "pointer",
                                                    whiteSpace: "nowrap",
                                                    textOverflow: "ellipsis",
                                                    outline: estaSeleccionado ? "3px solid #fff" : "none",
                                                    boxShadow: estaSeleccionado ? "0 0 0 3px #1e293b, 0 0 0 6px #fff" : "none",
                                                    boxSizing: "border-box",
                                                    transition: "background-color 0.15s",
                                                },
                                                onClick: () => { }, // clic en tarjeta no hace nada
                                                className: 'gantt-item-fusion',  //estilo de la tarjeta
                                            })}
                                            title={`${item.title}${item.reservaDetalles?.nombre_agente ? ` · Solicitado por: ${item.reservaDetalles.nombre_agente}` : ''}`}
                                        >
                                            <span
                                                className="flex items-center mr-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                                title="Ver detalles"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!tarjetaParaMover) handleItemClick(item.id);
                                                }}
                                            >
                                                {iconoPorStatusGanttic(statusGanttic)}
                                            </span>
                                            {esFusion && (
                                                <span
                                                    title={`${numDias} días fusionados`}
                                                    style={{
                                                        background: 'rgba(0,0,0,0.25)',
                                                        borderRadius: '3px',
                                                        fontSize: '8px',
                                                        fontWeight: 700,
                                                        padding: '1px 4px',
                                                        marginRight: '4px',
                                                        flexShrink: 0,
                                                        lineHeight: 1,
                                                        letterSpacing: '0.03em',
                                                    }}
                                                >{numDias}d</span>
                                            )}
                                            <div className="flex flex-col leading-none overflow-hidden flex-1 min-w-0 gap-[1px]">
                                                <span className="text-[10px] font-bold truncate leading-none">
                                                    {(() => {
                                                        const primerNombre = (item.reservaDetalles?.nombre_agente || '').split(' ')[0];
                                                        const empresa = item.title.toUpperCase();
                                                        const sufijo = primerNombre ? ` / ${primerNombre}` : '';
                                                        if (isMobile) {
                                                            const base = `${item.reservaDetalles.ticket ?? ''} - ${item.title.slice(0, 10).toUpperCase()}${item.title.length > 10 ? '...' : ''}`;
                                                            return `${base}${sufijo}`;
                                                        }
                                                        return `${empresa}${sufijo}`;
                                                    })()}
                                                </span>
                                                {density !== 'ultra' && ciudad && (
                                                    <span className="text-[8px] opacity-75 truncate leading-none">
                                                        {ciudad}
                                                    </span>
                                                )}
                                            </div>
                                            {isAdmin && !isMobile && (puedeMover || puedeExtender) && (
                                                <div className="flex gap-0.5 ml-1 flex-shrink-0">
                                                    {/* Botón mover día — solo si el día no tiene reporte */}
                                                    {puedeMover && (
                                                        <button
                                                            title="Mover a otro día"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (esFusion) { setDiaPickerState({ modo: 'mover', itemFusionado: item }); return; }
                                                                setTarjetaParaMover(estaSeleccionado && tarjetaParaMover?.modo === 'mover' ? null : { item, modo: 'mover' });
                                                            }}
                                                            style={{
                                                                background: estaSeleccionado && tarjetaParaMover?.modo === 'mover' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.2)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                color: '#fff',
                                                                cursor: 'pointer',
                                                                width: 22,
                                                                height: 22,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 13,
                                                                padding: 0,
                                                            }}
                                                        >↔</button>
                                                    )}
                                                    {/* Botón eliminar día — solo si el día no tiene reporte */}
                                                    {puedeMover && (
                                                        <button
                                                            title="Eliminar este día"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (esFusion) { setDiaPickerState({ modo: 'eliminar', itemFusionado: item }); return; }
                                                                handleEliminarDia(item);
                                                            }}
                                                            style={{
                                                                background: 'rgba(220,38,38,0.7)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                color: '#fff',
                                                                cursor: 'pointer',
                                                                width: 22,
                                                                height: 22,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 12,
                                                                padding: 0,
                                                            }}
                                                        >🗑</button>
                                                    )}
                                                    {/* Botón agregar día — solo si la reserva no está Finalizada */}
                                                    {puedeExtender && (
                                                        <button
                                                            title="Agregar un día más"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (esFusion) { setDiaPickerState({ modo: 'extender', itemFusionado: item }); return; }
                                                                setTarjetaParaMover(estaSeleccionado && tarjetaParaMover?.modo === 'extender' ? null : { item, modo: 'extender' });
                                                            }}
                                                            style={{
                                                                background: estaSeleccionado && tarjetaParaMover?.modo === 'extender' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.2)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                color: '#fff',
                                                                cursor: 'pointer',
                                                                width: 22,
                                                                height: 22,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 13,
                                                                padding: 0,
                                                            }}
                                                        >+</button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            >
                                <TimelineHeaders>
                                    <DateHeader unit="week" labelFormat={formatWeekHeaderEsMx} />
                                    <DateHeader unit="day" labelFormat={formatDayHeaderEsMx} />
                                    {vistaActiva === 'dia' && (
                                        <DateHeader
                                            unit="hour"
                                            labelFormat={([start]) => {
                                                const d = toJsDate(start);
                                                return d ? format(d, "H") : "";
                                            }}
                                            style={{ height: 20 }}
                                        />
                                    )}
                                </TimelineHeaders>
                            </Timeline>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* DETAIL DRAWER */}
            {openModal && (
                <>
                    <div className="overlay z-[1100]" onClick={handleCloseDetails} />
                    <aside className="gantt-detail-drawer fixed inset-y-0 right-0 z-[1110] h-full w-full max-w-md bg-white border-l border-zinc-200 shadow-2xl overflow-y-auto">
                        {tareaSeleccionada && (
                            <div className="p-6">
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <div>
                                        <h2 className="text-xl font-bold text-zinc-900 break-words">
                                            {tareaSeleccionada.title}
                                        </h2>
                                        <p className="text-sm text-zinc-500 mt-1">
                                            Detalles del servicio e itinerario
                                        </p>
                                    </div>
                                    <button onClick={handleCloseDetails} className="btn-icon">
                                        <HiOutlineXMark className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start justify-between gap-4 py-2 border-b border-zinc-100">
                                        <span className="text-sm text-zinc-500">Ticket</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-zinc-800">{tareaSeleccionada.reservaDetalles.ticket ?? "Sin checada"}</span>
                                            {isAdmin && tareaSeleccionada.reservaDetalles.reservas_id && (
                                                <a
                                                    href={
                                                        tareaSeleccionada.reservaDetalles.status_reserva === 'Apartado'
                                                            ? `/completar_apartado/${tareaSeleccionada.reservaDetalles.reservas_id}/actividad`
                                                            : `/editar_solicitud/${tareaSeleccionada.reservaDetalles.reservas_id}/${tareaSeleccionada.reservaDetalles.ticket ? 'aprobados' : 'pendientes'}`
                                                    }
                                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                                >
                                                    Ver solicitud
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    {[
                                        { label: "Equipo", value: tareaSeleccionada.reservaDetalles.equipo || "Sin equipo" },
                                        { label: "Tecnico", value: (() => {
                                            const d = tareaSeleccionada.reservaDetalles;
                                            // Si el día tiene reporte, mostrar el técnico del reporte
                                            if (d.dia_tiene_reporte && d.tecnico) return d.tecnico.trim();
                                            // Si no, mostrar el técnico asignado a la reserva (por correo)
                                            const asignado = gruposOriginales.find(g => g.correo === (d.correo_tecnico_reserva || d.correo_tecnico));
                                            return asignado?.title || d.tecnico?.trim() || 'Sin asignar';
                                        })() },
                                        { label: "Contacto", value: tareaSeleccionada.reservaDetalles.contacto || "Sin contacto" },
                                        { label: "Solicitado por", value: reservaCompleta?.nombre_agente || tareaSeleccionada.reservaDetalles.nombre_agente || "Sin agente" },
                                        { label: "Tipo de servicio", value: tareaSeleccionada.reservaDetalles.tipo_servicio || "Sin tipo" },
                                        { label: "Status", value: tareaSeleccionada.reservaDetalles.status_reserva || "Sin status" },
                                    ].map((item) => (
                                        <div key={item.label} className="flex items-start justify-between gap-4 py-2 border-b border-zinc-100">
                                            <span className="text-sm text-zinc-500">{item.label}</span>
                                            <span className="text-sm font-medium text-zinc-800 text-right break-words">{item.value}</span>
                                        </div>
                                    ))}

                                    <div className="py-2 border-b border-zinc-100">
                                        <p className="text-xs text-zinc-500 mb-1">Direccion</p>
                                        <p className="text-sm font-medium text-zinc-800 break-words">
                                            {tareaSeleccionada.reservaDetalles.domicilio || "Sin direccion"}
                                        </p>
                                    </div>

                                    {!esEventoInterno(tareaSeleccionada.reservaDetalles.tipo_servicio) && tareaSeleccionada.reservaDetalles.ticketid && (
                                        <a
                                            href={`https://desk.zoho.com/agent/artecnologiasadecv/soporte-técnico-remoto/tickets/details/${tareaSeleccionada.reservaDetalles.ticketid}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            Ver ticket en Zoho Desk
                                        </a>
                                    )}
                                </div>

                                <div className="mt-6">
                                    <h3 className="section-title mb-3">Itinerario</h3>
                                    {esEventoInterno(tareaSeleccionada.reservaDetalles.tipo_servicio) && <p className="text-sm text-zinc-500 mb-3">Gestiona este registro desde el panel de eventos internos.</p>}
                                    {itinerarioEdits.length > 0 ? (
                                        <div className="space-y-3">
                                            {itinerarioEdits.map((it) => {
                                                const bloqueado = !!it.id_reporte || isAdmin === false || esEventoInterno(tareaSeleccionada.reservaDetalles.tipo_servicio);
                                                return (
                                                    <div key={it.id} className={`rounded-2xl border px-4 py-3 ${bloqueado ? 'bg-zinc-100 border-zinc-200 opacity-70' : 'bg-zinc-50 border-zinc-200'}`}>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-sm font-semibold text-zinc-900 capitalize">
                                                                {safeFormat(it.fecha, "EEEE d 'de' MMMM yyyy")}
                                                            </p>
                                                            {bloqueado && (
                                                                <span className="text-[10px] font-medium text-zinc-400 flex items-center gap-1">
                                                                    <HiNoSymbol className="w-3 h-3" /> Con reporte
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1">
                                                                <label className="text-[10px] text-zinc-400 font-medium">Entrada</label>
                                                                <input
                                                                    type="time"
                                                                    step="60"
                                                                    disabled={bloqueado}
                                                                    value={it.entrada?.slice(0, 5) || ""}
                                                                    onChange={e => setItinerarioEdits(prev => prev.map(e2 => e2.id === it.id ? { ...e2, entrada: e.target.value + ":00", modificado: true } : e2))}
                                                                    className="w-full mt-0.5 rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-800 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                                />
                                                            </div>
                                                            <div className="flex-1">
                                                                <label className="text-[10px] text-zinc-400 font-medium">Salida</label>
                                                                <input
                                                                    type="time"
                                                                    step="60"
                                                                    disabled={bloqueado}
                                                                    value={it.salida?.slice(0, 5) || ""}
                                                                    onChange={e => setItinerarioEdits(prev => prev.map(e2 => e2.id === it.id ? { ...e2, salida: e.target.value + ":00", modificado: true } : e2))}
                                                                    className="w-full mt-0.5 rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-800 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                                />
                                                            </div>
                                                            {!bloqueado && it.modificado && (
                                                                <div className="flex-shrink-0 self-end">
                                                                    <button
                                                                        onClick={() => handleGuardarHorario(it)}
                                                                        disabled={guardandoHorario === it.id}
                                                                        className="mb-0.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-60 transition-colors"
                                                                    >
                                                                        {guardandoHorario === it.id ? "..." : "Guardar"}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : reservaCompleta && !reservaCompleta.itinerario?.length ? (
                                        <div className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm text-zinc-500">
                                            No hay itinerario registrado.
                                        </div>
                                    ) : (
                                        <div className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm text-zinc-400 animate-pulse">
                                            Cargando itinerario...
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </aside>
                </>
            )}
        </div>
    );
}

export default ComponenteGantt;
